import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Paper,
    Divider,
    Stack,
    CircularProgress,
    Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useSessionClass } from '../../contexts/SessionClassContext';
import { useStudents } from '../../hooks/useStudents';
import { useTeacher } from '../../hooks/useTeacher';
import { MonthType, useAttendance } from '../../hooks/useAttendance';
import { useAuth } from '../../hooks/useAuth';
import { useTenantHeader } from '../../hooks/useTenantHeader';
import { useHolidays } from '../../hooks/useHolidays';
import { useError } from '../../contexts/ErrorContext';
import { format } from 'date-fns';

export interface AttendanceSlideModalProps {
    open: boolean;
    onClose: () => void;
    presetTeacherId?: number;
    presetMonth?: MonthType;
    presetYear?: number;
    presetDay?: number;
}

const MONTH_ORDER: MonthType[] = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];

const monthToIndex = (m?: MonthType) => {
    const idx = MONTH_ORDER.indexOf(m || 'JANUARY');
    return idx >= 0 ? idx : 0;
};

const AttendanceSlideModal: React.FC<AttendanceSlideModalProps> = ({
    open,
    onClose,
    presetTeacherId,
    presetMonth,
    presetYear,
    presetDay
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showError } = useError();
    const schoolId = user?.schoolId;
    const { selectedSession, selectedClass } = useSessionClass();

    const [step, setStep] = useState(0); // 0: Setup, 1 to N: Students, N+1: Summary
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [selectedTeacherId, setSelectedTeacherId] = useState<number | ''>('');
    const [month, setMonth] = useState<MonthType>('MAY');
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [day, setDay] = useState<number>(1);
    const [studentAttendance, setStudentAttendance] = useState<Record<number, string>>({});
    const [lastNotifiedSelection, setLastNotifiedSelection] = useState<string>('');

    const { tenantLoading } = useTenantHeader();
    const { teachersQuery } = useTeacher(schoolId ?? undefined);
    const { useStudentsBySessionIdAndClassId } = useStudents(tenantLoading);
    const { bulkAttendanceMutation, attendanceStatusQuery } = useAttendance();
    const { useholidaysQueryBySession } = useHolidays(selectedSession?.id);

    const monthAttendanceQuery = useAttendance(
        selectedClass?.id && selectedSession?.id
            ? { classId: selectedClass.id, sessionId: selectedSession.id, month, year }
            : undefined
    ).getAttendanceQuery;

    // Helper: check if attendance exists for the exact selected day in the fetched month data
    const attendanceExistsForSelectedDay = useMemo(() => {
        // Don't report existence while the query is still fetching fresh data
        if (monthAttendanceQuery.isFetching || attendanceStatusQuery.isLoading) return false;
        const data = monthAttendanceQuery.data || [];
        const statusIdToCode = new Map<number, string>(
            (attendanceStatusQuery.data || []).map(s => [s.id, String(s.statusCode).toUpperCase()])
        );
        return data.some(r => {
            // Strictly match month and year to avoid stale cached data from another month
            if (r.month !== month || r.year !== year) return false;
            const v: any = (r as any).dayStatusMap?.[String(day)] ?? (r as any).dayStatusMap?.[day];
            if (v == null) return false;
            const code = statusIdToCode.get(Number(v));
            if (code === 'NOT-MARKED') return false;
            return true;
        });
    }, [monthAttendanceQuery.data, monthAttendanceQuery.isFetching, attendanceStatusQuery.data, attendanceStatusQuery.isLoading, month, year, day]);

    // Watch for existing attendance and show notification — only when data has settled
    useEffect(() => {
        if (!open) return;
        // Don't show the error while data is still loading/refetching for the new date
        if (monthAttendanceQuery.isFetching) return;

        const selectionKey = `${selectedClass?.id}-${selectedSession?.id}-${month}-${year}-${day}`;

        if (attendanceExistsForSelectedDay) {
            if (lastNotifiedSelection !== selectionKey) {
                showError(`${t('attendance_already_exist_for_this_date')}`);
                setLastNotifiedSelection(selectionKey);
            }
        } else {
            // Date has no attendance — clear so we can re-notify if user comes back to an existing date
            if (lastNotifiedSelection === selectionKey) {
                setLastNotifiedSelection('');
            }
        }
    }, [open, attendanceExistsForSelectedDay, selectedClass?.id, selectedSession?.id, month, year, day, monthAttendanceQuery.isFetching, t, showError, lastNotifiedSelection]);

    const studentsQuery = useStudentsBySessionIdAndClassId(
        selectedSession?.id,
        selectedClass?.id
    );

    const filteredStudents = useMemo(() => {
        if (!studentsQuery.data) return [];
        return studentsQuery.data.filter(s => s.status !== false)
            .sort((a, b) => (a.studentName || '').toLowerCase().localeCompare((b.studentName || '').toLowerCase()));
    }, [studentsQuery.data]);

    const isDateDisabled = (date: Date) => {
        if (!date || isNaN(date.getTime())) return false;
        // Disallow future dates for attendance marking
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        if (d > today) return true;
        // Sundays are weekly school holidays
        if (date.getDay() === 0) return true;
        if (!useholidaysQueryBySession.data) return false;
        const dateStrForHoliday = format(date, "dd-MM-yyyy");
        return useholidaysQueryBySession.data.some((holiday) => {
            const isDateMatch = holiday.dates.includes(dateStrForHoliday);
            if (!isDateMatch) return false;
            return holiday.forAllClasses || holiday.classIds?.includes(selectedClass?.id || 0);
        });
    };

    const selectedDateObj = useMemo(() => {
        const mIdx = monthToIndex(month);
        return new Date(year, mIdx, day);
    }, [month, year, day]);

    const isSelectedDayDisabled = useMemo(() => {
        if (!selectedDateObj || isNaN(selectedDateObj.getTime())) return false;
        return isDateDisabled(selectedDateObj);
    }, [selectedDateObj, useholidaysQueryBySession.data, selectedClass?.id]);

    // Reset state when the modal opens
    useEffect(() => {
        if (open) {
            const now = new Date();
            setMonth(presetMonth || 'MAY');
            setYear(presetYear || now.getFullYear());
            setDay(presetDay || now.getDate());

            if (presetTeacherId) {
                setSelectedTeacherId(presetTeacherId);
            } else if (user && teachersQuery.data) {
                const currentTeacher = teachersQuery.data.find(t => t.userId === user.id);
                setSelectedTeacherId(currentTeacher ? currentTeacher.id : '');
            } else {
                setSelectedTeacherId('');
            }

            setStep(presetTeacherId || presetDay || presetMonth || presetYear ? 1 : 0);
            setStudentAttendance({});
            setLastNotifiedSelection('');
        }
    }, [open, user, teachersQuery.data, presetTeacherId, presetMonth, presetYear, presetDay]);

    // Clear studentAttendance when the user changes date selections (month/year/day)
    // so that stale attendance from a previous date doesn't carry over
    useEffect(() => {
        if (!open) return;
        setStudentAttendance({});
        setLastNotifiedSelection('');
    }, [month, year, day]);

    // Populate studentAttendance from fetched data ONLY after the query has settled
    useEffect(() => {
        if (!open) return;
        // Wait for the query to finish fetching so we don't populate from stale/cached data
        if (monthAttendanceQuery.isFetching) return;

        const statusIdToCode = new Map<number, string>(
            (attendanceStatusQuery.data || []).map(s => [s.id, String(s.statusCode).toUpperCase()])
        );
        const attendanceMap: Record<number, string> = {};
        (monthAttendanceQuery.data || []).forEach((record: any) => {
            // Strictly match the selected month and year
            if (record.month !== month || record.year !== year) return;
            const v = record?.dayStatusMap?.[String(day)] ?? record?.dayStatusMap?.[day];
            if (v == null) return;
            const code = statusIdToCode.get(Number(v));
            if (code && code !== 'NOT-MARKED') attendanceMap[record.studentId] = code;
        });
        setStudentAttendance(attendanceMap);
    }, [open, day, month, year, monthAttendanceQuery.data, monthAttendanceQuery.isFetching, attendanceStatusQuery.data]);



    const handleStatusSelect = (studentId: number, status: string) => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setStudentAttendance(prev => ({ ...prev, [studentId]: status }));
        // Auto-slide to next
        setTimeout(() => {
            setStep(prev => prev + 1);
            setIsTransitioning(false);
        }, 300);
    };

    const handleNext = () => setStep(prev => prev + 1);
    const handlePrev = () => setStep(prev => prev - 1);

    const summaryData = useMemo(() => {
        const total = filteredStudents.length;
        const presents = Object.values(studentAttendance).filter(s => s === 'PRESENT').length;
        const absents = Object.values(studentAttendance).filter(s => s === 'ABSENT').length;
        const leaves = Object.values(studentAttendance).filter(s => s === 'LEAVE').length;
        return { total, presents, absents, leaves };
    }, [filteredStudents, studentAttendance]);

    const handleSubmit = () => {
        if (Object.keys(studentAttendance).length < filteredStudents.length) {
            showError("Please Mark Attendance of all students.");
            return;
        }

        const payload = {
            staffId: Number(selectedTeacherId),
            classId: Number(selectedClass?.id),
            sessionId: Number(selectedSession?.id),
            month,
            year,
            students: Object.entries(studentAttendance).map(([id, code]) => {
                const status = attendanceStatusQuery.data?.find(s => s.statusCode.toUpperCase() === code);
                return {
                    studentId: Number(id),
                    day,
                    statusId: status?.id || 0
                };
            })
        };

        bulkAttendanceMutation.mutate(payload, {
            onSuccess: () => onClose(),
            onError: (err: any) => showError(err?.response?.data?.message || t('failed_to_submit_attendance'))
        });
    };

    const renderStepContent = () => {
        if (step === 0) {
            return (
                <Stack spacing={4} sx={{ mt: 2, p: 2 }}>
                    <Typography variant="h6" align="center" color="textSecondary">
                        Select Date and Teacher
                    </Typography>
                    {isSelectedDayDisabled ? (
                        <Typography variant="body2" color="error" align="center" sx={{ mt: -2 }}>
                            Attendance is disabled for the selected date (Sunday / Holiday).
                        </Typography>
                    ) : null}
                    <Stack direction="row" spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>{t('month')}</InputLabel>
                            <Select
                                value={month}
                                onChange={(e) => setMonth(e.target.value as MonthType)}
                                label={t('month')}
                            >
                                {MONTH_ORDER.map(m => (
                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>{t('year')}</InputLabel>
                            <Select
                                value={year}
                                onChange={(e) => setYear(Number(e.target.value))}
                                label={t('year')}
                            >
                                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>{t('day')}</InputLabel>
                            <Select
                                value={day}
                                onChange={(e) => setDay(Number(e.target.value))}
                                label={t('day')}
                            >
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                    <MenuItem key={d} value={d}>{d}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <FormControl fullWidth>
                        <InputLabel>{t('teacher_name')}</InputLabel>
                        <Select
                            value={selectedTeacherId}
                            onChange={(e) => setSelectedTeacherId(e.target.value as number)}
                            label={t('teacher_name')}
                        >
                            {/* Only include current user as an fallback option if no teacher record matches their userId */}
                            {user && !teachersQuery.data?.some(t => t.userId === user.id) && (
                                <MenuItem key={`user-${user.id}`} value={user.id}>
                                    {user.username}
                                </MenuItem>
                            )}
                            {teachersQuery.data?.map((teacher) => (
                                <MenuItem key={teacher.id} value={teacher.id}>
                                    {teacher.firstName} {teacher.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            );
        }

        if (step > 0 && step <= filteredStudents.length) {
            const student = filteredStudents[step - 1];
            const currentStatus = studentAttendance[student.id!];

            return (
                <Fade in={true} key={step}>
                    <Stack spacing={4} alignItems="center" sx={{ mt: 4, p: 2 }}>
                        <Typography variant="h4" fontWeight="bold" align="center">
                            {student.studentName}
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            {t('roll_number')}: {student.rollNumber || 'N/A'} | Student {step} of {filteredStudents.length}
                        </Typography>

                        <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
                            <Button
                                variant={currentStatus === 'PRESENT' ? 'contained' : 'outlined'}
                                color="success"
                                size="large"
                                onClick={() => handleStatusSelect(student.id!, 'PRESENT')}
                                disabled={isSelectedDayDisabled}
                                sx={{ py: 2, px: 4, borderRadius: 3, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {t('present')}
                            </Button>
                            <Button
                                variant={currentStatus === 'ABSENT' ? 'contained' : 'outlined'}
                                color="error"
                                size="large"
                                onClick={() => handleStatusSelect(student.id!, 'ABSENT')}
                                disabled={isSelectedDayDisabled}
                                sx={{ py: 2, px: 4, borderRadius: 3, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {t('absent')}
                            </Button>
                            <Button
                                variant={currentStatus === 'LEAVE' ? 'contained' : 'outlined'}
                                color="warning"
                                size="large"
                                onClick={() => handleStatusSelect(student.id!, 'LEAVE')}
                                disabled={isSelectedDayDisabled}
                                sx={{ py: 2, px: 4, borderRadius: 3, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {t('leave')}
                            </Button>
                        </Stack>
                    </Stack>
                </Fade>
            );
        }

        if (step > filteredStudents.length) {
            return (
                <Fade in={true}>
                    <Stack spacing={3} alignItems="center" sx={{ mt: 2, p: 2 }}>
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80 }} />
                        <Typography variant="h5" fontWeight="bold">{t('attendance_summary')}</Typography>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, width: '100%' }}>
                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography>{t('total_students')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.total}</Typography>
                                </Stack>
                                <Divider />
                                <Stack direction="row" justifyContent="space-between" color="success.main">
                                    <Typography>{t('present')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.presents}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" color="error.main">
                                    <Typography>{t('absent')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.absents}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" color="warning.main">
                                    <Typography>{t('leave')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.leaves}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {summaryData.total > Object.keys(studentAttendance).length && "Some students are missing attendance!"}
                        </Typography>
                    </Stack>
                </Fade>
            );
        }
    };

    return (
        <Dialog open={open} onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                onClose();
            }
        }} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#fcfcfc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{t('one_by_one_attendance')}</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                    {renderStepContent()}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fcfcfc', justifyContent: 'space-between' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handlePrev}
                    disabled={step === 0}
                    color="inherit"
                >
                    {t('previous')}
                </Button>

                {step > filteredStudents.length ? (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        color="primary"
                        disabled={
                            isSelectedDayDisabled ||
                            bulkAttendanceMutation.isLoading ||
                            Object.keys(studentAttendance).length < filteredStudents.length
                        }
                        sx={{ px: 4, borderRadius: 2 }}
                    >
                        {bulkAttendanceMutation.isLoading ? <CircularProgress size={24} /> : "OK"}
                    </Button>
                ) : (
                    <Button
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleNext}
                        disabled={step === 0 && (!selectedTeacherId || !month || !year || !day || isSelectedDayDisabled)}
                        color="inherit"
                    >
                        {t('next')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AttendanceSlideModal;
